package com.shadowings.gero.presentation.component.quiz

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.lerp
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInRoot
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.data.repository.UniliteralRepository.resFromGardinerCode
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.QuizQuestionType
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.WordSpellingContent
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.component.flattenGardinerCodes
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.hieroglyphTint
import com.shadowings.gero.presentation.ui.testModeCorrect
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.quiz_ripasso_traslitterazione
import gero.composeapp.generated.resources.quiz_show_options_button
import gero.composeapp.generated.resources.share_quiz_spelling
import gero.composeapp.generated.resources.word_spelling_correct_word_label
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

@DevicePreviews
@Composable
private fun WordSpellingComposablePreview() {
    AppTheme {
        Surface {
            WordSpellingComposable(
                quiz = WordSpellingContent(
                    prompt = "Scrivi questa parola: melograno",
                    gardiner = "M17-N35:O4:Y5-M1",
                    blocks = listOf("O4", "N35", "X1", "M1", "M17", "Y5", "A1"),
                    transliteration = "inhemet"
                ),
                comboCount = remember { mutableStateOf(0) },
                quizList = remember { mutableStateOf(emptyList()) },
                page = 0,
                pagerState = androidx.compose.foundation.pager.rememberPagerState { 1 },
                navController = rememberNavController(),
                quizResults = remember { mutableStateOf(emptyMap()) },
                errorsCount = remember { mutableStateOf(0) },
                testMode = false,
                userData = UserData(),
            )
        }
    }
}

private data class FlyingGlyph(
    val code: String,
    val pendingIndex: Int,
    val start: Offset,
    val startSize: IntSize,
    val end: Offset,
    val endSize: IntSize,
)

@Composable
fun WordSpellingComposable(
    quiz: WordSpellingContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    testMode: Boolean,
    userData: UserData,
    lessonId: String = "",
    hideOptions: Boolean = false,
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    onAnswerResult: ((isCorrect: Boolean, record: WrongAnswerRecord) -> Unit)? = null,
) {
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val hapticFeedback = LocalHapticFeedback.current
    val deviceInfo = rememberDeviceInfo()
    val isLandscape = deviceInfo.isLandscape
    val density = LocalDensity.current

    val correctCodes = remember(quiz.gardiner) { flattenGardinerCodes(quiz.gardiner) }

    // Ordered picks into quiz.blocks — the same "shift back, no gaps" trick WordSelectionComposable
    // uses for its word bank: dropping the last picked index and re-rendering in order naturally
    // compacts everything, with no explicit gap-filling logic needed.
    var selectedIndices by rememberSaveable(quiz.id, stateSaver = IntListSaver) {
        mutableStateOf(emptyList<Int>())
    }
    var revealed by rememberSaveable(quiz.id) { mutableStateOf(false) }
    val placedCodes = remember(selectedIndices, quiz.blocks) { selectedIndices.map { quiz.blocks[it] } }

    // On-screen positions (root-relative) of every bank block and every MdCWord slot, kept up to
    // date via onGloballyPositioned — this is how the picked-glyph "flies" from its bank position
    // to its target slot without needing MdCWord itself to know about shared-element transitions
    // (it's used in 50+ places in the app; making it SharedTransitionScope-aware would be a much
    // bigger, riskier change than tracking coordinates and animating a floating overlay copy).
    val bankPositions = remember(quiz.id) { mutableStateMapOf<Int, Offset>() }
    val bankSizes = remember(quiz.id) { mutableStateMapOf<Int, IntSize>() }
    val slotPositions = remember(quiz.id) { mutableStateMapOf<Int, Offset>() }
    val slotSizes = remember(quiz.id) { mutableStateMapOf<Int, IntSize>() }
    var containerOrigin by remember(quiz.id) { mutableStateOf(Offset.Zero) }
    var flyingGlyph by remember(quiz.id) { mutableStateOf<FlyingGlyph?>(null) }
    // Re-created (starting fresh at 0f) every time flyingGlyph changes, instead of being reset via
    // a LaunchedEffect's snapTo — that reset would only land a dispatch AFTER the recomposition
    // that first renders the overlay, so for the second+ flight in the same question the overlay
    // would flash at the PREVIOUS flight's leftover value (1f, i.e. at the slot) for one frame.
    val flightProgress = remember(quiz.id, flyingGlyph) { Animatable(0f) }

    LaunchedEffect(flyingGlyph) {
        val glyph = flyingGlyph ?: return@LaunchedEffect
        flightProgress.animateTo(1f, animationSpec = tween(280, easing = FastOutSlowInEasing))
        // Only now does the real slot reveal the glyph and the bank block vanish for good —
        // until this point both showed an empty placeholder and the overlay carried the glyph.
        selectedIndices = selectedIndices + glyph.pendingIndex
        flyingGlyph = null
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .onGloballyPositioned { containerOrigin = it.positionInRoot() }
    ) {

        val promptText = quiz.prompt.resolve()

        val questionContent = @Composable {
            QuestionBox(
                question = promptText,
                questionTestTag = "word_spelling_question",
                modifier = Modifier.padding(vertical = 12.dp),
                wordContent = {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Box(
                            modifier = Modifier
                                .clickable(enabled = state.pending && selectedIndices.isNotEmpty()) {
                                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                    selectedIndices = selectedIndices.dropLast(1)
                                }
                        ) {
                            // hide=true + override reuses MdCWord's own layout/scaling exactly, so slot
                            // sizes always match the real word — placed glyphs render normally, everything
                            // beyond them stays invisible (but still occupies its cell) until picked.
                            MdCWord(
                                word = quiz.gardiner,
                                rtl = false,
                                cart = false,
                                size = 64.dp,
                                testTag = "word_spelling_slots",
                                contentDescription = placedCodes.joinToString(" ").ifBlank { null },
                                hide = true,
                                override = placedCodes,
                                cellModifier = { flatIndex ->
                                    Modifier.onGloballyPositioned { coords ->
                                        slotPositions[flatIndex] = coords.positionInRoot()
                                        slotSizes[flatIndex] = coords.size
                                    }
                                }
                            )
                        }
                        // Wrong answer: show the real word visually instead of spelling out its
                        // Gardiner codes in the bottom feedback card — the user never memorized
                        // those codes, so reading e.g. "M17 N35 O4" back at them isn't useful.
                        if (state.showResult && !state.correct) {
                            Spacer(modifier = Modifier.height(8.dp))
                            Text(
                                text = stringResource(Res.string.word_spelling_correct_word_label),
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            MdCWord(
                                word = quiz.gardiner,
                                rtl = false,
                                cart = false,
                                size = 64.dp,
                                testTag = "word_spelling_correct_word"
                            )
                        }
                    }
                }
            )
        }

        val optionsContent = @Composable {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (hideOptions && !revealed) {
                RevealOptionsButton(onClick = { revealed = true })
                } else {
                FlowRow(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    val hasFreeSlot = selectedIndices.size < correctCodes.size
                    quiz.blocks.forEachIndexed { index, code ->
                        // Placed (or mid-flight) blocks become an empty placeholder instead of
                        // disappearing, so the bank's item count/positions never shift around —
                        // same reasoning as WordSelectionComposable's WordPlaceholder.
                        val isPlaced = index in selectedIndices || index == flyingGlyph?.pendingIndex
                        Box(
                            modifier = Modifier.onGloballyPositioned { coords ->
                                bankPositions[index] = coords.positionInRoot()
                                bankSizes[index] = coords.size
                            }
                        ) {
                            if (isPlaced) {
                                GlyphPlaceholder()
                            } else {
                                GlyphBlock(
                                    code = code,
                                    testModeHighlight = testMode && code in correctCodes,
                                    enabled = state.pending && hasFreeSlot && flyingGlyph == null,
                                    onClick = {
                                        if (hasFreeSlot && flyingGlyph == null) {
                                            hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                            val targetFlatIndex = selectedIndices.size
                                            val start = bankPositions[index]
                                            val startSize = bankSizes[index]
                                            val end = slotPositions[targetFlatIndex]
                                            val endSize = slotSizes[targetFlatIndex]
                                            flyingGlyph = if (start != null && startSize != null && end != null && endSize != null) {
                                                FlyingGlyph(code, index, start, startSize, end, endSize)
                                            } else {
                                                // Positions not known yet (e.g. very first frame) —
                                                // fall back to placing it immediately, no animation.
                                                null
                                            }
                                            if (flyingGlyph == null) {
                                                selectedIndices = selectedIndices + index
                                            }
                                        }
                                    }
                                )
                            }
                        }
                    }
                }
                }
            }
        }

        QuizContentScaffold(
            testTag = "word_spelling_content",
            isLandscape = isLandscape,
            questionContent = { questionContent() },
            optionsContent = { optionsContent() }
        )

        flyingGlyph?.let { glyph ->
            val t = flightProgress.value
            val currentPos = lerp(glyph.start, glyph.end, t) - containerOrigin
            val currentWidthPx = androidx.compose.ui.util.lerp(glyph.startSize.width, glyph.endSize.width, t)
            val currentHeightPx = androidx.compose.ui.util.lerp(glyph.startSize.height, glyph.endSize.height, t)
            resFromGardinerCode(glyph.code)?.let { res ->
                Image(
                    modifier = Modifier
                        .offset { IntOffset(currentPos.x.roundToInt(), currentPos.y.roundToInt()) }
                        .size(
                            width = with(density) { currentWidthPx.toDp() },
                            height = with(density) { currentHeightPx.toDp() }
                        ),
                    painter = painterResource(res),
                    contentScale = ContentScale.Fit,
                    contentDescription = null,
                    colorFilter = ColorFilter.tint(hieroglyphTint)
                )
            }
        }

        val userAnswer = placedCodes.joinToString(" ").ifBlank { "_" }
        val quizInfo = """
            Quiz ID: ${quiz.id}
            Type: Word Spelling Quiz
            Prompt: $promptText
            Gardiner: ${quiz.gardiner}
            Transliteration: ${quiz.transliteration ?: ""}
            Correct codes: ${correctCodes.joinToString(" ")}
            Blocks: ${quiz.blocks.joinToString(", ")}
        """.trimIndent()

        QuizBottomFeedbackAndButton(
            modifier = Modifier.align(Alignment.BottomCenter),
            state = state,
            buttonEnabled = selectedIndices.size == correctCodes.size,
            // No correctAnswer text here: the correct word is shown visually (as hieroglyphs)
            // right below the user's attempt instead — raw Gardiner codes like "M17 N35 O4"
            // mean nothing to someone who hasn't memorized them.
            correctAnswer = null,
            info = quiz.transliteration?.let {
                stringResource(Res.string.quiz_ripasso_traslitterazione, it)
            },
            userData = userData,
            lessonId = lessonId,
            quizInfo = quizInfo,
            userAnswer = userAnswer,
            shareText = stringResource(Res.string.share_quiz_spelling),
            onConfirmClick = {
                val isCorrect = placedCodes == correctCodes
                onAnswerResult?.invoke(
                    isCorrect,
                    WrongAnswerRecord(
                        id = WrongAnswerRecord.buildId(lessonId, quiz.gardiner),
                        lessonId = lessonId,
                        type = QuizQuestionType.WORD_SPELLING,
                        questionText = promptText,
                        correctAnswerText = correctCodes.joinToString(" "),
                        userAnswerText = userAnswer,
                        hieroglyphs = quiz.gardiner,
                        options = quiz.blocks
                    )
                )
                isCorrect
            }
        )
    }
}

@Composable
private fun RevealOptionsButton(onClick: () -> Unit) {
    val hapticFeedback = LocalHapticFeedback.current
    Box(
        modifier = Modifier.fillMaxWidth().padding(vertical = 24.dp),
        contentAlignment = Alignment.Center
    ) {
        Button(
            onClick = {
                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                onClick()
            }
        ) {
            Text(text = stringResource(Res.string.quiz_show_options_button))
        }
    }
}

@Composable
private fun GlyphBlock(
    code: String,
    testModeHighlight: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    OutlinedCard(
        onClick = onClick,
        enabled = enabled,
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.outlinedCardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant,
            contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
            disabledContainerColor = MaterialTheme.colorScheme.surfaceVariant,
            disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant,
        ),
        border = BorderStroke(
            width = if (testModeHighlight) 2.dp else 1.dp,
            color = if (testModeHighlight) testModeCorrect else MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
        ),
        modifier = Modifier
            .size(64.dp)
            .semantics { contentDescription = code; testTag = code }
    ) {
        Box(
            modifier = Modifier.fillMaxSize().padding(8.dp),
            contentAlignment = Alignment.Center
        ) {
            resFromGardinerCode(code)?.let { res ->
                Image(
                    modifier = Modifier.fillMaxSize(),
                    painter = painterResource(res),
                    contentScale = ContentScale.Fit,
                    contentDescription = null,
                    colorFilter = ColorFilter.tint(hieroglyphTint)
                )
            }
        }
    }
}

/**
 * Reserves a bank block's exact footprint once its glyph has been placed (or is mid-flight),
 * so the FlowRow never reflows the remaining blocks — mirrors WordSelectionComposable's
 * WordPlaceholder.
 */
@Composable
private fun GlyphPlaceholder() {
    OutlinedCard(
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.outlinedCardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.05f),
            contentColor = Color.Transparent,
            disabledContainerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.05f),
            disabledContentColor = Color.Transparent
        ),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.1f)),
        modifier = Modifier.size(64.dp)
    ) {}
}
